import { CommonModule, Location, ViewportScroller } from '@angular/common';
import { Component, Input, OnInit, WritableSignal, inject, signal } from '@angular/core';
import { DocumentReference } from '@angular/fire/firestore';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { NavigationComponent } from '../../components/navigation/navigation.component';
import { AssessmentsService } from '../../services/assessments.service';
import { BbsData, BbsDataSet, initDataBBS } from '../../services/model';
import { getDateIsoString, getDateIsoStringPlus1Minute } from '../../services/util.functions';
import { AssessmentBaseComponent } from '../assessment-base/assessment-base.component';
import { DynamicTableInputBbsComponent } from './dynamic-table-input-bbs/dynamic-table-input-bbs.component';

@Component({
  selector: 'app-bbs',
  standalone: true,
  imports: [
    DynamicTableInputBbsComponent,
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatButtonModule,
    MatCardModule,
    ReactiveFormsModule,
    MatRadioModule,
    MatChipsModule,
    MatSelectModule,
    MatTableModule,
    RouterModule,
    MatTooltipModule,
    MatIconModule,
    NavigationComponent,
  ],
  templateUrl: './bbs.component.html',
  styleUrl: './bbs.component.scss',
})
export class BbsComponent extends AssessmentBaseComponent implements OnInit {
  location: Location = inject(Location);
  @Input() assessmentId: string | undefined;
  protected readonly anleitung =
    'Die Berg Balance Scale (Berg-Balance-Skala, BBS, auch Berg Balance Score) ist ein von K. O. Berg 1989 erstmals beschriebenes Testverfahren, mit dem das Gleichgewichtsverhalten anhand von 14 kurzen, praktischen Tests in 20 Minuten festgestellt werden kann.\n\n' +
    '\n\n';
  private assessmentService: AssessmentsService = inject(AssessmentsService);
  private scroller: ViewportScroller = inject(ViewportScroller);
  private fb: FormBuilder = inject(FormBuilder);
  assessmentData: WritableSignal<BbsData | undefined> = signal<BbsData | undefined>(undefined);
  assessmentDataNew: BbsData = JSON.parse(JSON.stringify(initDataBBS));
  hasError = signal({
    bbs: false,
  });
  hasMissingInputs = signal({
    bbs: true,
  });

  hasTriedToSave = signal(false);

  form: FormGroup = this.fb.group({
    assessmentTime: [getDateIsoStringPlus1Minute(), Validators.required],
    note: [''],
  });

  onBbsData(data: { data: Array<BbsDataSet>; note: string; hasError: boolean; hasMissingInputs: boolean }) {
    this.assessmentDataNew.bbs.data = data.data;
    this.assessmentDataNew.bbs.note = data.note;
    this.hasError.update(current => ({
      ...current,
      bbs: data.hasError,
    }));
    this.hasMissingInputs.update(current => ({
      ...current,
      bbs: data.hasMissingInputs,
    }));
  }

  ngOnInit(): void {
    this.scroller.scrollToPosition([0, 0]);

    if (this.assessmentId) {
      this.assessmentService.getAssessment(this.assessmentId).then(data => {
        // patch taskInfo
        const subData: BbsData = data.data as BbsData;
        const source = initDataBBS.bbs.data;
        const target = subData.bbs.data;
        for (let index = 0; index < target.length; index++) {
          const targetDS: BbsDataSet = target[index];
          const sourceDS: BbsDataSet = source[index];
          targetDS.taskInfo = sourceDS.taskInfo;
        }

        this.assessmentData.set(data.data as BbsData);
        const date = getDateIsoString(new Date(data.dateIso));
        this.form.controls['assessmentTime'].patchValue(date);

        let note = data.note;
        const hasNewNote = note && note.length > 0;
        if (!hasNewNote) {
          // try migration
          note = subData.bbs.note;
          subData.bbs.note = '';
        }
        this.form.controls['note'].patchValue(note);

        this.assessmentDataNew = JSON.parse(JSON.stringify(this.assessmentData()));
      });
    } else {
      this.assessmentData.set(initDataBBS);
      this.assessmentDataNew = JSON.parse(JSON.stringify(this.assessmentData()));
    }
  }
  onSave() {
    this.hasTriedToSave.set(true);
    if (this.hasError().bbs || this.hasMissingInputs().bbs) {
      return;
    }

    if (this.assessmentId) {
      this.assessmentService
        .updateAssessment(this.assessmentId, {
          type: 'BBS',
          dateIso: this.form.controls['assessmentTime'].value,
          note: this.form.controls['note'].value ?? '',
          data: this.assessmentDataNew,
          // score: this.calculateTotalScore(),
        })
        .then(res => {
          this.router.navigate(['../../../reports/assessments'], {
            relativeTo: this.route,
            queryParams: { type: 'BBS', dId: this.assessmentId },
          });
        });
    } else {
      this.assessmentService
        .createAssessment({
          type: 'BBS',
          dateIso: this.form.controls['assessmentTime'].value,
          note: this.form.controls['note'].value ?? '',
          data: this.assessmentDataNew,
          // score: this.calculateTotalScore(),
        })
        .then((docRef: DocumentReference) => {
          this.router.navigate(['../../reports/assessments'], {
            relativeTo: this.route,
            queryParams: { type: 'BBS', dId: docRef.id },
          });
        });
    }
  }

  onNavigateBack() {
    if (this.assessmentId) {
      // this.router.navigate(['../../../reports/assessments'], { relativeTo: this.route });
      this.location.back();
    } else {
      // this.router.navigate(['../../reports/assessments'], { relativeTo: this.route });
      this.router.navigate(['../../assessments/new'], { relativeTo: this.route });
    }
  }

  calculateScores() {
    this.assessmentDataNew.bbs.data.map(test => {
      test.score = test.value !== null ? 4 - test.value : null;
      return test;
    });
    this.assessmentDataNew.bbs.score = this.calculateTotalScore();
  }

  /**
   0: Schädigung nicht vorhanden (0 - 4 %)
   1: Schädigung leicht ausgeprägt (5 - 24 %)
   2: Schädigung mäßig ausgeprägt (25 - 49 %)
   3: Schädigung erheblich ausgeprägt (50 - 95 %)
   4: Schädigung voll ausgeprägt (96 - 100 %)
   */

  calculateTotalScore(): number {
    const maxValue: number = 14 * 4;
    const totalValue: number = this.assessmentDataNew.bbs.data.reduce((sum: number, test) => {
      return sum + (test.value ?? 0);
    }, 0);
    // invertieren, da maxValue eine 0% Schädigung = 0 ergeben würde.
    const inversedTotalValue = maxValue - totalValue;
    const percent = (inversedTotalValue / maxValue) * 100;
    console.log('percent ' + percent);

    if (percent >= 0 && percent <= 4) {
      // return "Schädigung nicht vorhanden (0 - 4 %)";
      return 0;
    } else if (percent >= 5 && percent <= 24) {
      // return "Schädigung leicht ausgeprägt (5 - 24 %)";
      return 1;
    } else if (percent >= 25 && percent <= 49) {
      // return 'Schädigung mäßig ausgeprägt (25 - 49 %)';
      return 2;
    } else if (percent >= 50 && percent <= 95) {
      // return 'Schädigung erheblich ausgeprägt (50 - 95 %)';
      return 3;
    } else if (percent >= 96 && percent <= 100) {
      // return 'Schädigung voll ausgeprägt (96 - 100 %)';
      return 4;
    } else {
      // return 'Wert außerhalb des gültigen Bereichs';
      return 8;
    }
  }
}
